import type { CellContext, ColumnDef } from '@tanstack/react-table'

import {
  getAssignableUsersAppOrganizationOrganizationsAssignableUsersGetOptions,
  getDistributorsAppOrganizationOrganizationsDistributorsGetOptions,
  getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey,
  updateDistributorRelationshipAppOrganizationOrganizationsDistributorsRelationshipIdPatchMutation,
} from '@/client/@tanstack/react-query.gen'
import { getDistributorsAppOrganizationOrganizationsDistributorsGet } from '@/client/sdk.gen'
import type { AssignableUser, DistributorOrganizationResponse } from '@/client/types.gen'
import { Badge } from '@/components/ui/badge'
import { ChatTrigger } from '@/components/chat/chat-trigger'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
} from '@/components/ui/card'
import type { ComboboxOption } from '@/components/ui/combobox'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from '@/hooks/use-auth'
import { useToast } from '@/hooks/use-toast'
import { formatUserDisplayName } from '@/lib/format-user-display-name'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUpDown, HelpCircle, Trash2 } from 'lucide-react'
import { lazy, useMemo } from 'react'

import { SettingsPageSkeleton } from '../settings/-components/skeleton'
import { CardHeaderWithBadge } from './-components/card-header-with-badge'
import { RemoveChannelPartnerDialog } from './-components/remove-channel-partner-dialog'

const ReactTableDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-table-devtools').then(m => ({ default: m.ReactTableDevtools })))
  : () => <></>

export const Route = createFileRoute(
  '/_authed/_user/organization/channel-partners',
)({
  component: ChannelPartnersSection,
  pendingComponent: SettingsPageSkeleton,
  errorComponent: () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">
          Error fetching channel partners
        </h3>
      </div>
    </div>
  ),
  beforeLoad: () => ({
    getTitle: () => 'Partners',
    getDescription: () => 'View your channel partners',
  }),
  loader: async ({ context }) => {
    // Skip API call if user has no organization (inherited from parent route)
    if (!context.hasOrganization) {
      return { distributors: [], noOrganization: true }
    }

    // Fetch channel partners (both suppliers and distributors)
    const response
      = await getDistributorsAppOrganizationOrganizationsDistributorsGet({
        throwOnError: false,
      })
    if (response.error) {
      if (response.response?.status === 403) {
        return { distributors: [], noOrganization: false }
      }
      throw response.error
    }
    return { distributors: response.data, noOrganization: false }
  },
})

/**
 * Cell component for editing the Default PoC for a distributor
 */
function DefaultPocCell({ row }: CellContext<DistributorOrganizationResponse, unknown>) {
  const { toast } = useToast()
  const { canManageDistributors: canEdit } = useAuth()
  const queryClient = useQueryClient()
  const partner = row.original

  // Fetch assignable users to get users from the partner's org
  // Note: This hook must be called unconditionally (before any early returns)
  const { data: assignableUsersData } = useQuery({
    ...getAssignableUsersAppOrganizationOrganizationsAssignableUsersGetOptions(),
  })

  // Filter to only show users from this partner's org
  const users = assignableUsersData?.users
  const partnerOrgId = partner.distributorOrgId
  const partnerUsers = useMemo(() => {
    if (!users)
      return []
    return users.filter(u => u.organizationId === partnerOrgId)
  }, [users, partnerOrgId])

  // Build combobox options
  const userOptions: ComboboxOption[] = useMemo(() => {
    return partnerUsers.map((user: AssignableUser) => ({
      value: user.id,
      label: formatUserDisplayName({
        firstName: user.firstName ?? undefined,
        lastName: user.lastName ?? undefined,
        email: user.email ?? undefined,
        fallback: 'Unknown User',
      }),
      metadata: user.email ?? undefined,
    }))
  }, [partnerUsers])

  // Find user name for optimistic update
  const findUserName = (userId: string): string | undefined => {
    const user = partnerUsers.find(u => u.id === userId)
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
    return name || undefined
  }

  // Mutation to update the default PoC with optimistic cache updates
  const updateMutation = useMutation({
    ...updateDistributorRelationshipAppOrganizationOrganizationsDistributorsRelationshipIdPatchMutation(),
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey(),
      })

      // Snapshot previous value
      const previousData = queryClient.getQueryData<DistributorOrganizationResponse[]>(
        getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey(),
      )

      // Optimistically update cache
      queryClient.setQueryData<DistributorOrganizationResponse[]>(
        getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey(),
        old => old?.map(d =>
          d.relationshipId === String(variables.path.relationship_id)
            ? {
                ...d,
                defaultPocUserId: variables.body.defaultPocUserId ?? undefined,
                defaultPocUserName: variables.body.defaultPocUserId
                  ? findUserName(variables.body.defaultPocUserId)
                  : undefined,
              }
            : d,
        ),
      )

      return { previousData }
    },
    onSuccess: () => {
      toast({
        title: 'Assignee updated',
        description: `Updated default assignee for ${partner.distributorName}`,
      })
    },
    onError: (error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(
          getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey(),
          context.previousData,
        )
      }
      toast({
        title: 'Unable to update assignee',
        description: error.detail?.[0]?.msg ?? 'Something went wrong. Please try again.',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      // Refetch to ensure server state
      void queryClient.invalidateQueries({
        queryKey: getDistributorsAppOrganizationOrganizationsDistributorsGetQueryKey(),
      })
    },
  })

  const handleChange = (newUserId: string) => {
    updateMutation.mutate({
      path: { relationship_id: Number(partner.relationshipId) },
      body: { defaultPocUserId: newUserId || undefined },
    })
  }

  // Wrapper to ensure consistent row height
  const cellWrapper = (content: React.ReactNode) => (
    <div className="min-w-52 h-9 flex items-center">
      {content}
    </div>
  )

  // Suppliers don't have a PoC from the distributor's perspective
  if (partner.direction === 'SUPPLIER') {
    return cellWrapper(
      <span className="text-muted-foreground text-sm px-3">—</span>,
    )
  }

  if (partnerUsers.length === 0) {
    return cellWrapper(
      <span className="text-muted-foreground text-sm px-3">
        No team members yet
      </span>,
    )
  }

  // Non-admins or distributors see read-only display
  if (!canEdit) {
    // Find the assigned user to display their name (with email fallback)
    const assignedUser = partner.defaultPocUserId
      ? partnerUsers.find(u => u.id === partner.defaultPocUserId)
      : undefined
    const displayName = assignedUser
      ? formatUserDisplayName({
          firstName: assignedUser.firstName ?? undefined,
          lastName: assignedUser.lastName ?? undefined,
          email: assignedUser.email ?? undefined,
          fallback: 'Unknown User',
        })
      : undefined

    return cellWrapper(
      <span className="text-sm px-3">
        {displayName || <span className="text-muted-foreground">Not set</span>}
      </span>,
    )
  }

  return cellWrapper(
    <Combobox
      options={userOptions}
      value={partner.defaultPocUserId ?? ''}
      placeholder="Select assignee..."
      searchPlaceholder="Search team members..."
      emptyMessage="No team members found"
      onValueChange={handleChange}
      disabled={updateMutation.isPending}
    />,
  )
}

/**
 * Cell component for the delete action — only shown to admins for DISTRIBUTOR-direction rows.
 * SUPPLIER-direction rows are relationships where we are the distributor, so we cannot delete them.
 */
function ActionsCell({ row }: CellContext<DistributorOrganizationResponse, unknown>) {
  const { canManageDistributors } = useAuth()
  const partner = row.original

  // Only supplier-org admins can delete; SUPPLIER direction means current org is the distributor
  if (!canManageDistributors || partner.direction === 'SUPPLIER') {
    return
  }

  return (
    <RemoveChannelPartnerDialog partner={partner}>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        title="Remove channel partner"
      >
        <Trash2 className="h-4 w-4" />
        <span className="sr-only">{`Remove ${partner.distributorName}`}</span>
      </Button>
    </RemoveChannelPartnerDialog>
  )
}

const columns: ColumnDef<DistributorOrganizationResponse>[] = [
  {
    accessorKey: 'distributorName',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          type="button"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Partner Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        <span className="font-medium whitespace-nowrap">
          {row.getValue('distributorName')}
        </span>
      )
    },
  },
  {
    accessorKey: 'direction',
    header: 'Type',
    cell: ({ row }) => {
      const direction = row.getValue('direction') as 'SUPPLIER' | 'DISTRIBUTOR'
      return (
        <Badge
          variant="outline"
          className={direction === 'SUPPLIER'
            ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800'
            : 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'}
        >
          {direction === 'SUPPLIER' ? 'Supplier' : 'Distributor'}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'defaultPocUserName',
    header: () => {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 cursor-help">
                <span>Default Assignee</span>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>When you share accounts with this partner, they'll automatically be assigned to this person.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
    cell: DefaultPocCell,
  },
  {
    id: 'actions',
    header: '',
    cell: ActionsCell,
  },
]

function ChannelPartnersSection() {
  const { isDistributorOrg } = useAuth()
  const loaderData = Route.useLoaderData()

  // Use React Query for channel partners (enables optimistic updates)
  // Loader data provides initial values for instant rendering
  const { data: partners } = useQuery({
    ...getDistributorsAppOrganizationOrganizationsDistributorsGetOptions(),
    initialData: loaderData.distributors,
    enabled: !isDistributorOrg, // Skip query for distributor orgs
  })

  // Fetch assignable users to determine which partners have team members
  const { data: assignableUsersData } = useQuery({
    ...getAssignableUsersAppOrganizationOrganizationsAssignableUsersGetOptions(),
    enabled: !isDistributorOrg, // Skip query for distributor orgs
  })

  // Sort partners: those with team members first, then alphabetically by name
  const sortedPartners = useMemo(() => {
    if (!partners)
      return []
    const users = assignableUsersData?.users ?? []

    return partners.toSorted((a, b) => {
      const aHasUsers = users.some(u => u.organizationId === a.distributorOrgId)
      const bHasUsers = users.some(u => u.organizationId === b.distributorOrgId)

      // Sort those with users first
      if (aHasUsers && !bHasUsers)
        return -1
      if (!aHasUsers && bHasUsers)
        return 1

      // Then alphabetically by name
      return (a.distributorName ?? '').localeCompare(b.distributorName ?? '')
    })
  }, [partners, assignableUsersData?.users])

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: sortedPartners,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  // DISTRIBUTOR tier orgs don't have their own channel partners (they ARE distributors)
  if (isDistributorOrg) {
    return (
      <Card>
        <CardHeaderWithBadge
          title="Channel Partners"
          description="View your distribution network and partner relationships."
        />
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Channel partner management is not available for distributor organizations.
              Your accounts are shared with you by your supplier partners.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeaderWithBadge
        title="Channel Partners"
        description="View your distribution network and partner relationships."
      />
      <CardContent className="space-y-4">
        {/* Filters and support message */}
        <div className="flex items-center justify-between gap-2">
          <Input
            placeholder="Filter by partner name..."
            value={(table.getColumn('distributorName')?.getFilterValue() as string) ?? ''}
            className="max-w-sm"
            onChange={event =>
              table.getColumn('distributorName')?.setFilterValue(event.target.value)}
          />

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>To modify channel partners, reach out to support</span>
            <ChatTrigger variant="outline" size="sm">
              Contact Support
            </ChatTrigger>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-md border">
          <ReactTableDevtools table={table} />
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="whitespace-nowrap">
                        {header.isPlaceholder
                          ? undefined
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="whitespace-nowrap">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No channel partners found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
